import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { emergencyReportService } from '../../services/emergencyReport.service';
import {
  EmergencyReport,
  CreateEmergencyReport,
  EmergencyType,
  EmergencyPriority,
  EmergencyStatus,
  getEmergencyTypeLabel,
  getEmergencyStatusLabel,
  getEmergencyPriorityLabel,
  getEmergencyPriorityColor,
  getEmergencyStatusColor,
} from '../../types';
import { toast } from '../../components/Toast';
import styles from './EmergencyReports.module.css';

export const EmergencyReports: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<EmergencyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<EmergencyReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateEmergencyReport>({
    siteId: user?.siteId || 0,
    type: EmergencyType.Other,
    title: '',
    description: '',
    priority: EmergencyPriority.Medium,
  });

  const [statusUpdate, setStatusUpdate] = useState({
    status: EmergencyStatus.Acknowledged,
    responseNotes: '',
  });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await emergencyReportService.getEmergencyReports({
        page: 1,
        pageSize: 50,
      });
      setReports(response.data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento delle segnalazioni';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await emergencyReportService.createEmergencyReport(formData);
      setShowCreateForm(false);
      setFormData({
        siteId: user?.siteId || 0,
        type: EmergencyType.Other,
        title: '',
        description: '',
        priority: EmergencyPriority.Medium,
      });
      loadReports();
      toast.success('Segnalazione creata con successo!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nella creazione della segnalazione';
      toast.error(message);
    }
  };

  const handleUpdateStatus = async (reportId: number) => {
    try {
      await emergencyReportService.updateStatus(reportId, statusUpdate);
      setSelectedReport(null);
      setStatusUpdate({
        status: EmergencyStatus.Acknowledged,
        responseNotes: '',
      });
      loadReports();
      toast.success('Stato aggiornato con successo!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nell\'aggiornamento dello stato';
      toast.error(message);
    }
  };

  const canManageReports = user && user.role <= 2; // Manager, Owner, SuperAdmin

  if (loading) {
    return <div className={styles.container}><p>Caricamento...</p></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>🚨 Segnalazioni Urgenze</h1>
        <button
          className={styles.primaryButton}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Annulla' : '+ Nuova Segnalazione'}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {showCreateForm && (
        <div className={styles.formCard}>
          <h2>Nuova Segnalazione</h2>
          <form onSubmit={handleCreateReport}>
            <div className={styles.formGroup}>
              <label>Tipo di Emergenza *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
                required
              >
                <option value={EmergencyType.Delay}>Ritardo</option>
                <option value={EmergencyType.Absence}>Assenza</option>
                <option value={EmergencyType.Illness}>Malattia</option>
                <option value={EmergencyType.Overcrowding}>Sovraffollamento</option>
                <option value={EmergencyType.EquipmentFailure}>Guasto attrezzature</option>
                <option value={EmergencyType.SafetyIssue}>Problema di sicurezza</option>
                <option value={EmergencyType.StaffShortage}>Carenza personale</option>
                <option value={EmergencyType.CustomerComplaint}>Reclamo cliente</option>
                <option value={EmergencyType.Other}>Altro</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Titolo *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Breve descrizione del problema"
                required
                maxLength={255}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Descrizione *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione dettagliata della situazione"
                required
                rows={4}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Priorità</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              >
                <option value={EmergencyPriority.Low}>Bassa</option>
                <option value={EmergencyPriority.Medium}>Media</option>
                <option value={EmergencyPriority.High}>Alta</option>
                <option value={EmergencyPriority.Critical}>Critica</option>
              </select>
            </div>

            <div className={styles.formActions}>
              <button type="submit" className={styles.primaryButton}>
                Invia Segnalazione
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setShowCreateForm(false)}
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.reportsList}>
        <h2>Le Tue Segnalazioni</h2>
        {reports.length === 0 ? (
          <p className={styles.emptyState}>Nessuna segnalazione trovata</p>
        ) : (
          <div className={styles.reportsGrid}>
            {reports.map((report) => (
              <div key={report.id} className={styles.reportCard}>
                <div className={styles.reportHeader}>
                  <h3>{report.title}</h3>
                  <div className={styles.badges}>
                    <span
                      className={styles.badge}
                      style={{ backgroundColor: getEmergencyPriorityColor(report.priority) }}
                    >
                      {getEmergencyPriorityLabel(report.priority)}
                    </span>
                    <span
                      className={styles.badge}
                      style={{ backgroundColor: getEmergencyStatusColor(report.status) }}
                    >
                      {getEmergencyStatusLabel(report.status)}
                    </span>
                  </div>
                </div>

                <div className={styles.reportBody}>
                  <p className={styles.reportType}>
                    📋 {getEmergencyTypeLabel(report.type)}
                  </p>
                  <p className={styles.reportDescription}>{report.description}</p>
                  <p className={styles.reportMeta}>
                    <span>👤 {report.reportedByUserName}</span>
                    <span>🏢 {report.siteName}</span>
                  </p>
                  <p className={styles.reportDate}>
                    📅 {new Date(report.createdAt).toLocaleString('it-IT')}
                  </p>

                  {report.responseNotes && (
                    <div className={styles.responseNotes}>
                      <strong>Risposta del responsabile:</strong>
                      <p>{report.responseNotes}</p>
                      {report.resolvedByUserName && (
                        <small>da {report.resolvedByUserName}</small>
                      )}
                    </div>
                  )}
                </div>

                {canManageReports && report.status !== 'Resolved' && report.status !== 'Cancelled' && (
                  <div className={styles.reportActions}>
                    <button
                      className={styles.manageButton}
                      onClick={() => setSelectedReport(report)}
                    >
                      Gestisci
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedReport && canManageReports && (
        <div className={styles.modal} onClick={() => setSelectedReport(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2>Gestisci Segnalazione</h2>
            <p><strong>{selectedReport.title}</strong></p>

            <div className={styles.formGroup}>
              <label>Nuovo Stato</label>
              <select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate({
                  ...statusUpdate,
                  status: parseInt(e.target.value),
                })}
              >
                <option value={EmergencyStatus.Acknowledged}>Presa in carico</option>
                <option value={EmergencyStatus.InProgress}>In gestione</option>
                <option value={EmergencyStatus.Resolved}>Risolta</option>
                <option value={EmergencyStatus.Cancelled}>Annullata</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Note di risposta</label>
              <textarea
                value={statusUpdate.responseNotes}
                onChange={(e) => setStatusUpdate({
                  ...statusUpdate,
                  responseNotes: e.target.value,
                })}
                placeholder="Aggiungi note sulla gestione..."
                rows={3}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.primaryButton}
                onClick={() => handleUpdateStatus(selectedReport.id)}
              >
                Aggiorna
              </button>
              <button
                className={styles.secondaryButton}
                onClick={() => setSelectedReport(null)}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
